import { createSelector } from 'reselect'
import { ADMIN, TEACHER } from './constants'
import { validateUserContextForProgram } from './helpers'

const DEFAULT_PROGRAM_TRACK = 'Instructor'

const programKeyToActiveSiteIdKey = {
  k5SelDigital: 'elementaryActiveSiteId',
  highSchool: 'hsActiveSiteId',
  mspDigital: 'middleSchoolActiveSiteId',
}

const selectUserContextManager = state => state.userContextManager
const selectRouter = state => state.router
// TODO: this also needs to be able to select MspDigital classes from the new v2 GET endpoint:
const selectMiddleSchoolClasses = state =>
  state.lmsManager?.instructorData?.MiddleSchool ??
  state.lmsManager?.instructorData?.Classes
const selectIsAdminOnly = (_, isAdminsOnly) => isAdminsOnly

function stripUserContext(userContext) {
  // none of these fields in the userContext are being used by our current services:
  delete userContext.location
  delete userContext.paks

  return userContext
}

const ContextNamespaces = {
  MSP: 'msp',
  K5: 'k5',
  HIGHSCHOOL: 'highschool',
  FAMILY: 'family',
  DWB: 'digitalWellBeing',
}

const getDigitalWellBeingNamespace = pathname => {
  const isDigitalWellBeingPath =
    pathname?.includes('/product/digital-well-being') ||
    pathname?.includes('/digital-well-being')

  return isDigitalWellBeingPath ? ContextNamespaces.DWB : ''
}

const getContextNamespace = pathname => {
  const digitalWellBeingNamespace = getDigitalWellBeingNamespace(pathname)

  if (digitalWellBeingNamespace) {
    return digitalWellBeingNamespace
  }

  if (
    pathname?.includes('product/middle-school') ||
    pathname?.includes('dashboard/grades-6-8')
  ) {
    return ContextNamespaces.MSP
  } else if (
    pathname?.includes('product/high-school') ||
    pathname?.includes('program/hs') ||
    pathname?.includes('dashboard/grades-9-12')
  ) {
    return ContextNamespaces.HIGHSCHOOL
  } else if (
    pathname?.includes('family-resources') ||
    pathname?.includes('family/high-school')
  ) {
    return ContextNamespaces.FAMILY
  } else if (
    pathname?.includes('product/elementary') ||
    pathname?.includes('dashboard/grades-k-5')
  ) {
    return ContextNamespaces.K5
  }
  return ''
}

/**
 * Selects the program context based on the current pathname.
 * This is a memoized selector using createSelector.
 */
export const selectProgramContext = createSelector(
  [selectUserContextManager, selectRouter, selectMiddleSchoolClasses],
  (userContextManager, router, middleSchoolClasses) => {
    let context = {}
    const pathname = router?.location?.pathname

    const contextNamespace = getContextNamespace(pathname)

    switch (contextNamespace) {
      case ContextNamespaces.MSP:
        const isPreview = pathname?.includes('preview')
        context = getMspContext(
          userContextManager,
          isPreview,
          middleSchoolClasses,
          router,
        )
        break
      case ContextNamespaces.HIGHSCHOOL:
        const queryParams = new URLSearchParams(router.location?.search)
        const isHighschoolPreview = queryParams.get('mode') === 'preview'
        context = getHighSchoolContext(userContextManager, isHighschoolPreview)
        break
      case ContextNamespaces.DWB:
        context = getDigitalWellBeingContextWithSiteSelection(userContextManager)
        break
      case ContextNamespaces.FAMILY:
        context = getFamilyResourcesContext({
          rawUserContext: userContextManager,
          path: pathname,
        })
        break
      case ContextNamespaces.K5:
        context = getK5Context(userContextManager)
        break
    }

    return context
  },
)

// TODO: Leaving this for legacy versions of k5GetContext and mspGetContext. Do we still need it?
/**
 * Ensures programTrack is set with default value when missing, null, or undefined
 * @param {Object} site - Site object to modify
 * @param {string} programKey - Program key (e.g., 'k5SelDigital', 'mspDigital')
 * @returns {Object} - Site object with programTrack set
 */
export function ensureProgramTrack(site, programKey) {
  if (!site?.programs?.[programKey]?.preferences) {
    return site
  }

  const preferences = site.programs[programKey].preferences
  if (preferences.programTrack == null) {
    preferences.programTrack = DEFAULT_PROGRAM_TRACK
  }

  return site
}

/**
 * Ensures preview mode settings and default values are set appropriately
 * @param {Object} site - Site object to modify
 * @param {string} programKey - Program key (e.g., 'k5SelDigital', 'mspDigital')
 * @param {boolean} isPreview - Whether the site is in preview mode
 * @returns {Object} - Site object with preview mode and default values set
 */
export function ensurePreviewModeAndDefaultPrefsForK8(
  site,
  programKey,
  isPreview,
) {
  if (!site?.programs?.[programKey]?.preferences) {
    return site
  }

  const preferences = site.programs[programKey]?.preferences || {}
  const claims = site.claims || []

  // Handle bullyingHarassmentUnitEnabled based on user role
  if (programKey === 'mspDigital') {
    if (
      preferences.bullyingHarassmentUnitEnabled == null ||
      preferences.bullyingHarassmentUnitEnabled === undefined ||
      isPreview
    ) {
      if (claims.includes(ADMIN)) {
        preferences.bullyingHarassmentUnitEnabled = true
      } else if (
        claims.includes(TEACHER) &&
        (preferences.bullyingHarassmentUnitEnabled === undefined ||
          preferences.bullyingHarassmentUnitEnabled === null)
      ) {
        preferences.bullyingHarassmentUnitEnabled = false
      }
    }
  }

  // For all below we want to show admins the most content so they can make selection choices.
  const isAdminAndPreview = claims.includes(ADMIN) && isPreview
  if (preferences.yearsTeaching == null || isAdminAndPreview) {
    // For years teaching 0-1 puts a user into "instructor" track so we want to show admins this in preview mode
    preferences.yearsTeaching = '0-1 year'
  }

  if (preferences.teachesLessons == null || isAdminAndPreview) {
    preferences.teachesLessons = true
  }

  if (preferences.programTrack == null || isAdminAndPreview) {
    // For program track we want to show admins the default track which is "instructor" so they can make selection choices.
    preferences.programTrack = DEFAULT_PROGRAM_TRACK
  }

  return site
}

/**
 * Checks if a site has an invalid active site ID that needs to be cleared
 * @param {Object} site - Site object from filterUserContextByProgram
 * @returns {Object|null} - Object with details about invalid site ID or null if valid
 */
export function detectInvalidActiveSiteId(site) {
  return site?._invalidActiveSiteIdDetected || null
}

/**
 * Helper function to get the action type for clearing an active site ID
 * @param {string} activeSiteIdKey - The key for the active site ID (e.g., 'elementaryActiveSiteId')
 * @returns {string|null} - The action type string or null if not found
 */
export function getActiveSiteIdActionType(activeSiteIdKey) {
  switch (activeSiteIdKey) {
    case 'elementaryActiveSiteId':
      return 'SET_ELEMENTARY_SITE_ID'
    case 'middleSchoolActiveSiteId':
      return 'SET_MIDDLE_SCHOOL_SITE_ID'
    case 'hsActiveSiteId':
      return 'SET_HIGH_SCHOOL_SITE_ID'
    case 'digitalWellBeingActiveSiteId':
      return 'SET_DIGITAL_WELL_BEING_SITE_ID'
    default:
      return null
  }
}

function isDigitalWellBeingValidSite(site) {
  return [ADMIN, TEACHER].some(claim => site?.claims?.includes(claim))
}

function getDigitalWellBeingContextWithSiteSelection(userContextManager) {
  const userContextClone = structuredClone(userContextManager || {})
  const sites = userContextClone.sites ?? []

  if (!sites.length) {
    return null
  }

  const validSites = sites.filter(isDigitalWellBeingValidSite)
  if (!validSites.length) {
    return null
  }

  const activeSiteId = userContextClone.digitalWellBeingActiveSiteId
  const selectedSite =
    validSites.find(site => site.siteId === activeSiteId) ?? validSites[0]

  return {
    ...stripUserContext(userContextClone),
    contextType: 'digitalWellBeing',
    digitalWellBeingActiveSiteId: selectedSite.siteId,
    sites: [structuredClone(selectedSite)],
  }
}

function filterFamilySites(sites, program) {
  return sites.filter(
    site =>
      site.claims.map(c => c.toLowerCase()).includes('family') &&
      site.programs.hasOwnProperty(program),
  )
}

// Removes the programs that are not desired from site object.
function withCleanPrograms(site, program) {
  return {
    ...site,
    programs: {
      [program]: site.programs?.[program],
    },
  }
}

// For now, we just need the first elementary site we can find
function resolveElementarySite(familySites) {
  return familySites?.[0]
}

// Send the first site that has H&B unit enabled (if exist), otherwise send the first site
function resolveMiddleSchoolSite(familySites) {
  const siteWithHB = familySites.find(
    site =>
      site.programs.mspDigital?.preferences?.bullyingHarassmentUnitEnabled ??
      false,
  )

  if (siteWithHB) {
    return siteWithHB
  }

  return familySites?.[0]
}

/**
 * Resolves a site object for Family HighSchool from a list of family sites.
 * The object contains agregated preferences from all sites. Where studentActivities
 * is set to true if any site has it set to true and implementationLevel is set to
 * the highest value found.
 * @param {Array<object>} familySites - List of family sites
 */
function resolveHighSchoolFamilySite(familySites) {
  const highSchoolFamilySiteSameSample = familySites?.[0]
  // Check if any site has studentActivities set to true
  const studentActivities = familySites.some(
    site =>
      site.programs[PROGRAMS_KEYS.HS]?.preferences?.studentActivities ?? false,
  )

  // Determine the highest implementationLevel
  const highestImplementationLevel = Math.max(
    ...familySites.map(
      site =>
        site.programs[PROGRAMS_KEYS.HS]?.preferences?.implementationLevel ?? 0,
    ),
  )

  // Assemble the site object
  return {
    claims: highSchoolFamilySiteSameSample?.claims,
    programs: {
      highSchool: {
        courses:
          highSchoolFamilySiteSameSample?.programs[PROGRAMS_KEYS.HS]?.courses,
        preferences: {
          ...highSchoolFamilySiteSameSample?.programs[PROGRAMS_KEYS.HS]
            ?.preferences,
          implementationLevel: highestImplementationLevel,
          studentActivities: studentActivities,
        },
      },
    },
  }
}

const PROGRAMS_KEYS = {
  K5: 'k5SelDigital',
  MSP: 'mspDigital',
  HS: 'highSchool',
}

function getProgramKey(path) {
  if (path.includes('elementary')) {
    return PROGRAMS_KEYS.K5
  } else if (path.includes('high-school')) {
    return PROGRAMS_KEYS.HS
  } else {
    return PROGRAMS_KEYS.MSP
  }
}

function getFamilyResourcesContext(input) {
  const { rawUserContext, path } = input

  if (!path || !rawUserContext?.sites) {
    return null
  }

  const programKey = getProgramKey(path)
  const familySites = filterFamilySites(rawUserContext.sites, programKey)

  if (!familySites || familySites.length === 0) {
    throw new Error('No family sites found. Must have at least one.')
  }

  let familySite
  if (programKey === PROGRAMS_KEYS.K5) {
    familySite = resolveElementarySite(familySites)
  } else if (programKey === PROGRAMS_KEYS.HS) {
    familySite = resolveHighSchoolFamilySite(familySites)
  } else {
    familySite = resolveMiddleSchoolSite(familySites)
  }

  const context = {
    ...rawUserContext,
    // We only want to send one site to the family resources page
    sites: [withCleanPrograms(familySite, programKey)],
  }

  return context
}

const _isBPUPreviewEnabled = claimsAndPreferences => {
  const claims = claimsAndPreferences.claims
  const preferences = claimsAndPreferences.preferences
  return claims.includes(ADMIN) && !preferences.bullyingHarassmentUnitEnabled
}

/**
 * Filters userContext to return a single site with a specific program
 * @param {Object} userContextManager - The userContextManager state object
 * @param {Object} options - Configuration options
 * @param {string} options.programKey - Key for the program to filter (e.g., 'highSchool')
 * @returns {Object|null} - Filtered site object or null if no valid site found
 * @throws {Error} - If no sites are available for the specified program
 */
export function filterUserContextByProgram(userContextManager, options) {
  const { programKey } = options || {}

  const activeSiteIdKey = programKeyToActiveSiteIdKey[programKey]

  // Validate that userContextManager has sites with the required program
  const validation = validateUserContextForProgram(
    userContextManager,
    programKey,
  )
  if (!validation.isValid) {
    console.warn(
      `no ${programKey} site available. ${validation.reason}. returning null.`,
    )
    return null
  }

  // Create a clone to avoid mutating the original state
  const userContextClone = structuredClone(userContextManager)
  const isFetching = userContextClone?.isFetching

  if (isFetching) {
    console.warn('userContext is fetching, returning null')
    return null
  }

  const sites = userContextClone?.sites
  let site = sites?.find(
    site =>
      site?.siteId === userContextClone[activeSiteIdKey] &&
      site?.programs?.[programKey],
  )

  if (!site) {
    console.warn(
      `Active site ID ${userContextClone[activeSiteIdKey]} for ${programKey} is invalid or no longer available`,
    )

    // Try to find any valid site with the required program as fallback
    // We specifically avoid family-only sites by requiring ADMIN or TEACHER claims
    const fallbackSite = sites?.find(
      site =>
        site?.programs?.[programKey] &&
        // we don't want to fallback to a family access site
        (site?.claims?.includes(ADMIN) || site?.claims?.includes(TEACHER)),
    )

    if (!fallbackSite) {
      console.warn(
        `no ${programKey} site available for fallback. returning null.`,
      )
      return null
    }

    console.warn(
      `Falling back to site ${fallbackSite.siteId} for ${programKey}`,
    )
    site = fallbackSite

    // Signal that the active site ID should be cleared by setting a flag
    // This allows the calling component to detect and clear the invalid site ID
    site._invalidActiveSiteIdDetected = {
      activeSiteIdKey,
      invalidSiteId: userContextClone[activeSiteIdKey],
      fallbackSiteId: fallbackSite.siteId,
    }
  }

  // Create a clone of the site to avoid mutating the original state
  const siteClone = structuredClone(site)

  // Filter to only include the specified program
  const programData = siteClone.programs?.[programKey]
  siteClone.programs = { [programKey]: programData }

  // Clean up the internal flag before returning
  if (siteClone._invalidActiveSiteIdDetected) {
    delete siteClone._invalidActiveSiteIdDetected
  }

  return siteClone
}

export const aggregateMSPSiteContext = userContextManager => {
  const userContextSites = userContextManager?.sites || []

  const mspClaims = new Set()
  const mspPreferences = {
    isSetup: false,
  }

  userContextSites.forEach(site => {
    // for middle school we want to include the highest level access for claims a user has AND exclude family claim from being returned
    if (site.claims) {
      mspClaims.add(...site.claims)
    }

    const siteMspPreferences = site?.programs?.mspDigital?.preferences
    if (siteMspPreferences) {
      mspPreferences.implementationLevel =
        mspPreferences.implementationLevel <
          siteMspPreferences.implementationLevel ||
        mspPreferences.implementationLevel == null
          ? siteMspPreferences.implementationLevel
          : mspPreferences.implementationLevel
      // if any site has bullyingHarassmentUnitEnabled, set it to true (otherwise, return the previous value of bullyingHarassmentUnitEnabled):
      mspPreferences.bullyingHarassmentUnitEnabled =
        !!siteMspPreferences.bullyingHarassmentUnitEnabled ||
        mspPreferences.bullyingHarassmentUnitEnabled == null
          ? siteMspPreferences.bullyingHarassmentUnitEnabled
          : mspPreferences.bullyingHarassmentUnitEnabled
      mspPreferences.isSetup = siteMspPreferences.isSetup
        ? siteMspPreferences.isSetup
        : mspPreferences.isSetup || false
    }
  })

  // remove nulls and undefineds from the set
  const nonNullClaims = Array.from(mspClaims).filter(value => value != null)

  return { claims: nonNullClaims, preferences: mspPreferences }
}

const getMspContext = (
  userContextManager,
  isPreview,
  middleSchoolClasses,
  router,
) => {
  // Create a deep clone of userContextManager to avoid mutating the original state
  const userContextClone = structuredClone(userContextManager || {})
  // Special case for lessons/teach path already handled in existing code
  if (!isPreview && router?.location?.pathname.includes('lessons/teach')) {
    const queryParams = new URLSearchParams(window?.location?.search || '')
    const courseInstanceId = queryParams.get('class')
    const matchingCourse = middleSchoolClasses?.find(
      course => course.instance === courseInstanceId,
    )
    if (!matchingCourse) {
      console.warn('no matching course found. Returning full user context.')
      return userContextClone
    }
    const siteWithCourse = structuredClone(
      userContextClone?.sites?.find(
        site => site.siteId === matchingCourse.siteId,
      ),
    )
    // Check if site exists before accessing its properties
    if (!siteWithCourse?.programs?.mspDigital) {
      console.warn(
        'Site with course not found or missing mspDigital program. Returning full user context.',
      )
      return userContextClone
    }
    const mspDigital = siteWithCourse?.programs?.mspDigital
    // get rid of all the other programs and just send middle school:
    siteWithCourse.programs = { mspDigital }
    // Ensure programTrack is set with default value
    ensureProgramTrack(siteWithCourse, 'mspDigital')

    return {
      ...stripUserContext(userContextClone),
      sites: [siteWithCourse],
    }
  }

  // If there's an active middle school site ID, use that site
  if (
    userContextClone?.middleSchoolActiveSiteId &&
    !userContextClone?.isFetching
  ) {
    const sites = userContextClone?.sites || []
    const site = sites.find(
      site =>
        site?.siteId === userContextClone?.middleSchoolActiveSiteId &&
        site?.programs?.mspDigital,
    )

    if (site) {
      const siteWithMsp = structuredClone(site)
      const mspDigital = siteWithMsp.programs?.mspDigital
      // get rid of all the other programs and just send middle school:
      siteWithMsp.programs = { mspDigital }
      // Ensure programTrack is set with default value
      ensureProgramTrack(siteWithMsp, 'mspDigital')

      return {
        ...stripUserContext(userContextClone),
        sites: [siteWithMsp],
      }
    }
  }

  // Fall back to the aggregated context behavior if no active site is selected or found
  const aggregateClaimsAndPreferences = aggregateMSPSiteContext(
    userContextClone,
  )
  const minimalUserContext = stripUserContext({
    ...userContextClone,
  })

  // if we're not in an instance, just always return the aggregated context for middle school with first MSP site + mutated preferences:
  const aggregatedSite = structuredClone(
    userContextClone?.sites?.find(site => site?.programs?.mspDigital),
  )

  if (!aggregatedSite) {
    console.error('No MSP site found in user context')
    return minimalUserContext // Return minimal context instead of throwing error
  }

  aggregatedSite.claims = aggregateClaimsAndPreferences.claims || []

  // null coalescing plug-in was causing problems but still needed to check
  // due to depth level concerns
  const { programs } = aggregatedSite || {}
  const { mspDigital: aggregatedMspDigital } = programs || {}

  if (!aggregatedMspDigital) {
    console.error('No mspDigital program found in aggregated site')
    return minimalUserContext
  }

  aggregatedMspDigital.preferences =
    aggregateClaimsAndPreferences.preferences || {}

  // This is telling all of program flex (not just route service) to deliver the B and H content (not just route tree).
  // If you tried to get to the page content for this from content-service AND from course manager both of those systems
  // should remove the B and H content (or deny you)
  const isBPUPreviewEnabled = _isBPUPreviewEnabled(
    aggregateClaimsAndPreferences,
  )
  const { preferences: aggregatedMspPreferences } = aggregatedMspDigital || {}

  if (aggregatedMspPreferences) {
    aggregatedMspPreferences.bullyingHarassmentUnitEnabled = aggregatedMspPreferences.bullyingHarassmentUnitEnabled
      ? aggregatedMspPreferences.bullyingHarassmentUnitEnabled
      : isBPUPreviewEnabled
  }

  const mspDigital = aggregatedMspDigital
  // get rid of all the other programs and just send middle school:
  aggregatedSite.programs = { mspDigital }
  // Ensure programTrack is set with default value
  ensureProgramTrack(aggregatedSite, 'mspDigital')
  return {
    ...minimalUserContext,
    sites: [aggregatedSite],
  }
}

/**
 * Ensures high school preferences are set with default values when missing or in preview mode.
 * Sets studentActivities to true and implementationLevel to 2 (highest level) by default.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.site - The site object containing high school program data
 * @param {boolean} params.isPreview - Whether the site is in preview mode
 * @returns {Object} A new site object with ensured high school preferences
 */
export const ensureHighSchoolPreferences = ({ site, isPreview }) => {
  // if Preferences are missing or in preview mode, set default preferences to highest level of content access
  let highSchoolPreferences = site?.programs?.highSchool?.preferences || {}

  if (
    highSchoolPreferences.studentActivities === undefined ||
    highSchoolPreferences.studentActivities === null ||
    isPreview
  ) {
    highSchoolPreferences.studentActivities = true
  }

  if (
    highSchoolPreferences.implementationLevel === undefined ||
    highSchoolPreferences.implementationLevel === null ||
    isPreview
  ) {
    highSchoolPreferences.implementationLevel = 2
  }

  const siteWithNewPrefs = structuredClone(site)
  siteWithNewPrefs.programs.highSchool.preferences = highSchoolPreferences
  const highSchool = siteWithNewPrefs.programs.highSchool
  // get rid of all the other programs and just send high school:
  siteWithNewPrefs.programs = { highSchool }

  return siteWithNewPrefs
}

const getHighSchoolContext = (userContextManager, isPreview) => {
  // Create a deep clone of userContextManager to avoid mutating the original state
  const userContextClone = structuredClone(userContextManager || {})

  // add in this hack to select a site id automatically as the first available high school site:
  // this is placeholder until we have user-selectable site ids
  if (!userContextClone?.hsActiveSiteId && !userContextClone?.isFetching) {
    const highSchoolSites = userContextClone?.sites?.filter(
      site => !!site.programs?.highSchool,
    )
    if (highSchoolSites.length === 0) {
      throw new Error('no high school site available')
    }
    userContextClone.hsActiveSiteId = highSchoolSites?.[0]?.siteId
  }
  const sites = userContextClone?.sites
  const site = sites?.find(
    site => site?.siteId === userContextClone.hsActiveSiteId,
  )

  if (!site) {
    console.warn('no high school site found.')
    return null
  }

  // Create a clone of the site to avoid mutating the original state
  const siteClone = structuredClone(site)

  // Make sure the highSchool program exists
  if (!siteClone?.programs?.highSchool) {
    console.warn(
      'Site has no highSchool program. Finding an alternative high school site.',
    )
    // Try to find an alternative high school site
    const alternativeHS = sites?.find(site => !!site?.programs?.highSchool)
    if (!alternativeHS) {
      throw new Error('no high school site available')
    }
    // Use the alternative site instead
    Object.assign(siteClone, structuredClone(alternativeHS))
  }

  // we don't want to include any program but highSchool in a High School user context:
  const highSchoolProgram = siteClone.programs?.highSchool
  siteClone.programs = { highSchool: highSchoolProgram }

  // Ensure preferences exists

  if (siteClone.programs?.highSchool?.courses) {
    // we don't want to include any course but highschool in a High School user context:
    siteClone.programs.highSchool.courses = siteClone.programs.highSchool.courses.filter(
      c => c?.name === 'highschool',
    )
  }

  const minimalUserContext = stripUserContext({
    ...userContextClone,
  })

  const arePreferencesMissing =
    !siteClone.programs?.highSchool?.preferences ||
    !siteClone.programs?.highSchool?.preferences?.studentActivities ||
    !siteClone.programs?.highSchool?.preferences?.implementationLevel

  // if Preferences are missing or in preview mode, set default preferences to highest level of content access
  if (arePreferencesMissing || isPreview) {
    if (isPreview) {
      console.log('hs isPreview mode is set. Setting default preferences.')
    }

    if (arePreferencesMissing) {
      console.log(
        'hs preferences are missing in userContext. Setting default preferences.',
      )
    }
    const siteWithNewPrefs = ensureHighSchoolPreferences({
      site: siteClone,
      isPreview,
    })
    return {
      ...minimalUserContext,
      sites: [siteWithNewPrefs],
      contextType: 'highschool',
    }
  }

  return {
    ...minimalUserContext,
    sites: [siteClone],
    contextType: 'highschool',
  }
}

const getK5Context = userContextManager => {
  // Create a deep clone of userContextManager to avoid mutating the original state
  const userContextClone = structuredClone(userContextManager || {})

  // add in this hack to select a site id automatically as the first available elementary site:
  // this is placeholder until we have user-selectable site ids
  // CASE: preview mode
  if (
    !userContextClone?.elementaryActiveSiteId &&
    !userContextClone?.isFetching
  ) {
    const elementarySites =
      userContextClone?.sites?.filter(site => !!site?.programs?.k5SelDigital) ||
      []
    if (elementarySites.length > 0) {
      userContextClone.elementaryActiveSiteId = elementarySites?.[0]?.siteId
    }
  }

  // If there's an active elementary site ID, use that site
  if (
    userContextClone?.elementaryActiveSiteId &&
    !userContextClone?.isFetching
  ) {
    const sites = userContextClone?.sites
    const site = sites?.find(
      site => site?.siteId === userContextClone?.elementaryActiveSiteId,
    )

    if (site && site?.programs?.k5SelDigital) {
      // Create a clone of the site to avoid mutating the original
      const siteClone = structuredClone(site)
      const k5SelDigital = siteClone?.programs?.k5SelDigital
      // get rid of all the other programs and just send k5:
      siteClone.programs = { k5SelDigital }
      // Ensure programTrack is set with default value
      ensureProgramTrack(siteClone, 'k5SelDigital')
      return {
        ...stripUserContext(userContextClone),
        sites: [siteClone],
      }
    }
  }

  // Fall back to the first k5 site if no active site is set or if it's not found
  const k5Site = userContextClone?.sites?.find(
    site => !!site?.programs?.k5SelDigital,
  )
  if (!k5Site) {
    console.warn('no k5 site found.')
    // don't fail here, just return full user context stripped down
    // (this is valid case for the K5 kit users):
    return stripUserContext(userContextClone)
  }

  // Create a clone of the site to avoid mutating the original
  const k5SiteClone = structuredClone(k5Site)
  const k5SelDigital = k5SiteClone?.programs?.k5SelDigital
  // get rid of all the other programs and just send k5:
  k5SiteClone.programs = { k5SelDigital }
  // Ensure programTrack is set with default value
  ensureProgramTrack(k5SiteClone, 'k5SelDigital')
  return {
    ...stripUserContext(userContextClone),
    sites: [k5SiteClone],
  }
}

// TODO: remove once sites/me surfaces sitePreferences.highSchool.areUsersAdded
const selectHighSchoolSiteIdAndPreferences = createSelector(
  [selectUserContextManager, selectRouter],
  (userContextManager, router) => {
    const pathname = router?.location?.pathname

    const isHighschool = pathname?.includes('admin/program/hs')

    if (isHighschool) {
      const highSchoolSites = userContextManager?.sites?.filter(
        site => !!site.programs.highSchool,
      )
      const hsSitesAndPreferences = highSchoolSites?.map(site => {
        return {
          siteId: site?.siteId,
          preferences: site?.programs?.highSchool?.preferences,
        }
      })
      return hsSitesAndPreferences
    }
    return null
  },
)

export const selectElementarySiteIdAndPreferences = createSelector(
  [selectUserContextManager, selectRouter],
  (userContextManager, router) => {
    const pathname = router?.location?.pathname
    const isElementary = pathname?.includes('product/elementary')

    if (isElementary) {
      const elementarySites = userContextManager?.sites?.filter(
        site => !!site.programs.k5SelDigital,
      )
      const elementarySitesAndPreferences = elementarySites?.map(site => {
        return {
          siteId: site?.siteId,
          preferences: site?.programs?.k5SelDigital?.preferences,
        }
      })
      return elementarySitesAndPreferences
    }
    return null
  },
)

export const selectMiddleSchoolSiteIdAndPreferences = createSelector(
  [selectUserContextManager, selectRouter],
  (userContextManager, router) => {
    const pathname = router?.location?.pathname
    const isMiddleSchool = pathname?.includes('product/middle-school')

    if (isMiddleSchool) {
      const middleSchoolSites = userContextManager?.sites?.filter(
        site => !!site.programs.mspDigital,
      )
      const mspSitesAndPreferences = middleSchoolSites?.map(site => {
        return {
          siteId: site?.siteId,
          preferences: site?.programs?.mspDigital?.preferences,
        }
      })
      return mspSitesAndPreferences
    }
    return null
  },
)

export function defaultUserContextFilter(userContext) {
  return userContext
}

export function filterUniqueUserContextFields(userContext) {
  // clone so we don't mutate siteId in the state tree
  const clonedContext = structuredClone(userContext)
  const filteredSiteId = clonedContext?.sites?.map(site => {
    site?.siteId && delete site.siteId

    return site
  })

  return { sites: filteredSiteId }
}

// consolidates claims and preferences to their
// "highest level"for MSP into one object
// {
//   claims: string[],
//   implementationLevel: number,
//   bullyingHarassmentUnitEnabled: boolean,
//   isSetup: boolean
// }
export const filterToAggregateMSPSiteContext = userContextSites => {
  if (!userContextSites) return
  return {
    claims: userContextSites?.reduce((mspContext, currentSite) => {
      const currentClaims = currentSite?.claims
      if (!currentClaims) {
        return mspContext
      }

      // we dont want "family" to be returned yet in the ms program fetch
      if (
        Array.isArray(currentClaims) &&
        (currentClaims.includes(ADMIN) || currentClaims.includes(TEACHER))
      ) {
        return currentClaims
      }

      return mspContext
    }, []),
    ...userContextSites?.reduce(
      (mspContext, currentSite) => {
        const currentMspPreferences =
          currentSite?.programs?.mspDigital?.preferences
        if (!currentMspPreferences) return mspContext
        // coalescing all the MSP preferences into one object where the highest value is set for any given preference
        mspContext.implementationLevel =
          (mspContext.implementationLevel !== undefined &&
            mspContext.implementationLevel <
              currentMspPreferences.implementationLevel) ||
          mspContext.implementationLevel === undefined
            ? currentMspPreferences.implementationLevel
            : mspContext.implementationLevel
        // if any site has bullyingHarassmentUnitEnabled, set it to true
        // (otherwise, return the previous value of bullyingHarassmentUnitEnabled):
        mspContext.bullyingHarassmentUnitEnabled =
          currentMspPreferences.bullyingHarassmentUnitEnabled === true
            ? true
            : mspContext.bullyingHarassmentUnitEnabled || false
        mspContext.isSetup =
          currentMspPreferences.isSetup === true
            ? true
            : mspContext.isSetup || false
        return mspContext
      },
      {
        implementationLevel: undefined,
        bullyingHarassmentUnitEnabled: false,
        isSetup: false,
      },
    ),
  }
}

export const selectMspSiteContexts = createSelector(
  [selectUserContextManager, selectIsAdminOnly],
  (userContextManager, isAdminsOnly = false) => {
    let mspSiteContexts = userContextManager?.sites?.reduce(
      (sitesWithMsp, currentSite) => {
        if (currentSite?.programs?.['mspDigital']) {
          if (isAdminsOnly && currentSite.claims?.includes(ADMIN)) {
            sitesWithMsp.push(currentSite)
          } else if (!isAdminsOnly) {
            sitesWithMsp.push(currentSite)
          }
        }
        return sitesWithMsp
      },
      [],
    )

    return mspSiteContexts
  },
)

/**
 * Selects the program context based on the current pathname.
 * This is a memoized selector using createSelector.
 *
 * TODO LEARN-23339 once we merged all the site selection flags and versions of the program in it we should replace the non site selection version of these functions with all of these
 */
export const selectProgramContextWithSiteSelection = createSelector(
  [selectUserContextManager, selectRouter, selectMiddleSchoolClasses],
  (userContextManager, router) => {
    let context = {}
    const pathname = router?.location?.pathname

    const contextNamespace = getContextNamespace(pathname)

    const isK8Preview = pathname?.includes('preview')

    const queryParams = new URLSearchParams(router?.location?.search)
    const isHighschoolPreview = queryParams.get('mode') === 'preview'

    switch (contextNamespace) {
      case ContextNamespaces.MSP:
        context = getMspContextWithSiteSelection(
          userContextManager,
          isK8Preview,
        )
        break
      case ContextNamespaces.HIGHSCHOOL:
        context = getHighSchoolContextWithSiteSelection(
          userContextManager,
          isHighschoolPreview,
        )
        break
      case ContextNamespaces.DWB:
        context = getDigitalWellBeingContextWithSiteSelection(userContextManager)
        break
      case ContextNamespaces.FAMILY:
        context = getFamilyResourcesContext({
          rawUserContext: userContextManager,
          path: pathname,
        })
        break
      case ContextNamespaces.K5:
        context = getK5ContextWithSiteSelection(userContextManager, isK8Preview)
        break
    }

    return context
  },
)

export const getMspContextWithSiteSelection = (
  userContextManager,
  isPreview,
) => {
  // Create a deep clone of userContextManager to avoid mutating the original state
  const userContextClone = structuredClone(userContextManager || {})
  // Special case for lessons/teach path already handled in existing code
  const middleSchoolSite = filterUserContextByProgram(userContextManager, {
    programKey: 'mspDigital',
  })

  // Return null if no valid site found
  if (!middleSchoolSite) {
    return null
  }

  // Ensure programTrack is set with default value
  ensurePreviewModeAndDefaultPrefsForK8(
    middleSchoolSite,
    'mspDigital',
    isPreview,
  )

  return {
    ...stripUserContext(userContextClone),
    sites: [middleSchoolSite],
  }
}

export const getHighSchoolContextWithSiteSelection = (
  userContextManager,
  isPreview,
) => {
  const userContextClone = structuredClone(userContextManager || {})

  const highSchoolSite = filterUserContextByProgram(userContextManager, {
    programKey: 'highSchool',
  })

  // Return null if no valid site found
  if (!highSchoolSite) {
    return null
  }

  const minimalUserContext = stripUserContext({
    ...userContextClone,
  })

  const arePreferencesMissing =
    !highSchoolSite.programs?.highSchool?.preferences ||
    highSchoolSite.programs?.highSchool?.preferences?.studentActivities ===
      undefined ||
    highSchoolSite.programs?.highSchool?.preferences?.implementationLevel ===
      undefined

  // if Preferences are missing or in preview mode, set default preferences to highest level of content access
  if (arePreferencesMissing || isPreview) {
    if (isPreview) {
      console.log('hs isPreview mode is set. Setting default preferences.')
    }

    if (arePreferencesMissing) {
      console.log(
        'hs preferences are missing in userContext. Setting default preferences.',
      )
    }

    const siteWithNewPrefs = ensureHighSchoolPreferences({
      site: highSchoolSite,
      isPreview,
    })

    return {
      ...minimalUserContext,
      sites: [siteWithNewPrefs],
      contextType: 'highschool',
    }
  }

  return {
    ...minimalUserContext,
    sites: [highSchoolSite],
    contextType: 'highschool',
  }
}

export const getK5ContextWithSiteSelection = (
  userContextManager,
  isPreview,
) => {
  // Create a deep clone of userContextManager to avoid mutating the original state
  const userContextClone = structuredClone(userContextManager || {})

  const elementarySite = filterUserContextByProgram(userContextManager, {
    programKey: 'k5SelDigital',
  })

  // Return null if no valid site found
  if (!elementarySite) {
    return null
  }

  // Ensure programTrack is set with default value
  ensurePreviewModeAndDefaultPrefsForK8(
    elementarySite,
    'k5SelDigital',
    isPreview,
  )

  return {
    ...stripUserContext(userContextClone),
    sites: [elementarySite],
  }
}

export default {
  selectUserContextManager,
  selectHighSchoolSiteIdAndPreferences,
  selectElementarySiteIdAndPreferences,
  selectMiddleSchoolSiteIdAndPreferences,
  selectProgramContext,
  selectProgramContextWithSiteSelection,
  selectMspSiteContexts,
  detectInvalidActiveSiteId,
  getActiveSiteIdActionType,
}
